import { AnswerTable, type EvidenceState } from '../components/assess/AnswerTable'
import { PageTabs, type PageTab } from '../components/kit'
import { answeredLine, reviewRows } from '../lib/answerReview'
import { useHashParam } from '../lib/hashParams'
import { request } from '../lib/api'
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  ApiError,
  api,
  can,
  daysUntil,
  pctValue,
  refusalReason,
  shortDate,
  type AnswerRow,
  type AssessmentDetail,
  type Assessment,
  type Clarification,
  type ClarificationList,
  type Issue,
  type Me,
  type Question,
  type RiskTypeCatalogueEntry,
  type Score,
  type ScoreStep,
} from '../lib/api'
import { afterEdit, fromServer, type Listed } from '../lib/issues'
import { useSave } from '../lib/save'
import { questionnaireWord, statusWord } from '../lib/status'
import { AssessmentActivity } from '../components/AssessmentActivity'
import { AssessmentEvidence } from '../components/AssessmentEvidence'
import { AssessmentFindings } from '../components/VendorFindings'
import { BandChip, BandWithAttestation } from '../components/BandChip'
import { BandFactors, stepLabel } from '../components/BandFactors'
import { CoverageCell, ErrorBox, Loading } from '../components/DataTable'
import { overrideBody } from '../components/footers'
import { Dialog, Status, Toggletip, Picker, type Tone, type PickerOption } from '../components/kit'
import { Crumbs } from '../components/Crumbs'
import '../styles/prose.css'
import '../styles/assessment.css'

// The review of one assessment: the questions and what came back, and the
// three things a reviewer does about them (ask about an answer, score, overrule
// the band).
//
// # WHY THE PAGE IS THE ANSWERS AND THE SCORE IS A PANEL
//
// The product officer's review of 17 September found this page showing the
// vendor's whole story at once: a progress track, every section, the answers
// and the band's reasoning side by side. A reviewer opens an assessment to read
// answers, so the answers are the page, and where the questionnaire stands is
// one line under the title. Scoring is a panel opened from the header: score
// now, rescore, why the band is what it is, the band override and the engine's
// own steps are all there, so nothing a reviewer could do before has gone.
// Every call, permission check and validation is the one the screen made before.

// A toggletip holds 200 characters. Text written for the screen fits already;
// this is for text that arrives from elsewhere (a refusal reason, a question's
// help), cut at a word so the bubble never covers what it explains. Wherever
// it is used the whole text is also on the control's title or in a drawer.
function tip(text: string): string {
  const t = text.trim()
  if (t.length <= 200) return t
  const cut = t.slice(0, 196)
  const space = cut.lastIndexOf(' ')
  return (space > 140 ? cut.slice(0, space) : cut) + '...'
}

// ------------------------------------------------------- where it is

// Where the questionnaire stands, as the one line under the title: how much
// is answered and what the record says happened to it. It replaces a four
// stage track, and it says nothing the track did not.
const OUT = new Set(['ISSUED', 'IN_PROGRESS', 'RETURNED'])

function progressLine(a: Assessment, counted: string, score: Score | undefined): string {
  const late = OUT.has(a.status) && (daysUntil(a.dueOn) ?? 0) < 0
  // How much is answered of what was asked (lib/answerReview.ts answeredLine):
  // a question the vendor was never shown is skipped, not unanswered.
  const parts = [counted]
  // The state in the vocabulary's word (lib/status.ts), lower case after the
  // count, with the one date that matters for it.
  const word = questionnaireWord(a.status, late).word.toLowerCase()
  switch (a.status) {
    case 'SUBMITTED':
    case 'UNDER_REVIEW':
      parts.push(word + (a.submittedAt ? ' ' + shortDate(a.submittedAt) : ''))
      break
    case 'SCORED':
      parts.push(word + (score?.computedAt ? ' ' + shortDate(score.computedAt) : ''))
      break
    default:
      if (a.status !== 'DRAFT') parts.push(word)
  }
  return parts.join(', ')
}

function statusLabel(a: Assessment): { text: string; tone: Tone } {
  const late = OUT.has(a.status) && (daysUntil(a.dueOn) ?? 0) < 0
  const s = questionnaireWord(a.status, late)
  return { text: s.word, tone: s.tone }
}

// The parts of the page (release 3.3), one on screen at a time.
type ReviewTab = 'answers' | 'evidence' | 'clarifications' | 'findings' | 'activity'

function isReviewTab(v: string | null): v is ReviewTab {
  return v === 'answers' || v === 'evidence' || v === 'clarifications' || v === 'findings' || v === 'activity'
}

// ------------------------------------------------------- one answer

// Where an answer came from and how it reads now live in lib/answerReview.ts,
// which the answers table and its tests share.

function isAnswered(a: AnswerRow | undefined): boolean {
  return Boolean(a && (a.isNa || (a.value !== undefined && a.value !== '')))
}

// ------------------------------------------------------- clarifications

// Section 11's loop, on the screen the reviewer is already on. Before this the
// only thing a reviewer could do about one answer they did not believe was
// hand the whole questionnaire back, which tells a vendor nothing about which
// of ninety answers was wrong.
//
// Outstanding rounds come first and settled ones below, because the first list
// is work and the second is record. A settled round is never hidden: "we
// asked, they explained, we accepted it" is the part an auditor reads. Each
// round opens in a drawer with the whole exchange and the decision.

// The clarification states in the vocabulary's words: a reply waiting for a
// decision is "Reply received", never "Decide" on the chip and "Accepted" in
// four other meanings elsewhere.
function clarifyLabel(status: string): { text: string; tone: Tone } {
  const s = statusWord('clarification', status)
  return { text: s.word, tone: s.tone }
}

// The questions as the picker lists them: found by key or by any word of the
// prompt. The value is the key, which is what a clarification is raised
// against, as it was the value of the select's option.
export function questionChoices(questions: Pick<Question, 'questionKey' | 'prompt'>[]): PickerOption[] {
  return questions.map((q) => ({ value: q.questionKey, label: q.prompt, code: q.questionKey }))
}

function AssessmentClarifications({
  assessmentId,
  questions,
  prefillKey,
  onPrefillUsed,
  onLoaded,
  onFailed,
}: {
  assessmentId: string
  questions: Question[]
  // A question key the Responses panel asked to be queried. Cleared as soon as
  // it is read, so pressing the same row twice works.
  prefillKey: string
  onPrefillUsed: () => void
  // Every list this panel loads, so the answers table marks the rows asked
  // about without asking the API a second time; and a read that failed, so
  // the page's tab can say so while the panel is hidden.
  onLoaded?: (list: Clarification[]) => void
  onFailed?: () => void
}) {
  const [list, setList] = useState<ClarificationList | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [actionError, setActionError] = useState<ApiError | null>(null)
  const [version, setVersion] = useState(0)
  const [questionKey, setQuestionKey] = useState('')
  const [asked, setAsked] = useState('')
  const [raiseOpen, setRaiseOpen] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const save = useSave(openId ? 'on ' + openId : raiseOpen ? 'ask' : null, 'the clarification')
  const busy = save.busy
  // One note per row rather than one for the panel, so a reviewer who typed a
  // rejection against one round and then looked at another does not send the
  // first one's words with the second one's decision.
  const [notes, setNotes] = useState<Record<string, string>>({})
  const canAsk = can('evidence.review')
  const choices = useMemo(() => questionChoices(questions), [questions])

  useEffect(() => {
    let live = true
    api
      .clarifications(assessmentId)
      .then((l) => {
        if (!live) return
        setList(l)
        setError(null)
      })
      .catch((err) => {
        if (live) setError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err)))
      })
    return () => {
      live = false
    }
  }, [assessmentId, version])

  useEffect(() => {
    if (list) onLoaded?.(list.clarifications)
  }, [list, onLoaded])
  useEffect(() => {
    if (error) onFailed?.()
  }, [error, onFailed])

  // A question handed over from a response row opens the form with that
  // question chosen, so nobody has to find it again in a select of ninety.
  useEffect(() => {
    if (!prefillKey) return
    setQuestionKey(prefillKey)
    setActionError(null)
    setRaiseOpen(true)
    onPrefillUsed()
  }, [prefillKey, onPrefillUsed])

  function run<T>(work: Promise<T>, then: () => void) {
    setActionError(null)
    save.send(work, {
      done: () => {
        then()
        setVersion((n) => n + 1)
      },
      failed: (err) => setActionError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))),
    })
  }


  function raise(e: FormEvent) {
    e.preventDefault()
    run(api.raiseClarification(assessmentId, { questionKey, asked: asked.trim() }), () => {
      setAsked('')
      setQuestionKey('')
      setRaiseOpen(false)
    })
  }


  function decide(c: Clarification, act: 'accept' | 'reject' | 'withdraw') {
    run(api.decideClarification(c.id, act, (notes[c.id] ?? '').trim()), () => setNotes((n) => ({ ...n, [c.id]: '' })))
  }


  if (error) return <ErrorBox error={error} what="The clarifications on this assessment" />
  if (!list) return <Loading what="the clarifications" />

  const settled = list.clarifications.filter((c) => c.settled)
  const ordered = [...list.outstanding, ...settled]
  const open = openId ? list.clarifications.find((c) => c.id === openId) ?? null : null
  // An open round, for somebody who may settle it: its dialog carries the note
  // and, in its footer, the decisions.
  const deciding = open !== null && !open.settled && canAsk

  const refusal = actionError && (
    <div className="error-box" role="alert">
      <h3>That did not go through ({actionError.code})</h3>
      <p>{actionError.message}</p>
      <p className="muted">{actionError.remedy}</p>
    </div>
  )

  return (
    <section className="panel" aria-labelledby="clarify-heading">
      <div className="prose-panelhead">
        <h2 id="clarify-heading">Clarifications</h2>
        {list.outstanding.length > 0 ? (
          <Status tone="crit">{list.outstanding.length} holding the score</Status>
        ) : list.clarifications.length === 0 ? (
          <Status tone="plain">None raised</Status>
        ) : (
          <Status tone="ok">All settled</Status>
        )}
        <Toggletip label="About clarifications">
          One question about one answer. An open round holds the score until it is settled; settled rounds stay on
          the record.
        </Toggletip>
        <span className="prose-grow" />
        {canAsk ? (
          <button
            type="button"
            onClick={() => {
              setActionError(null)
              setRaiseOpen(true)
            }}
          >
            Ask about an answer
          </button>
        ) : (
          <>
            <Status tone="neutral" title={refusalReason('evidence.review')}>
              Read only
            </Status>
            <Toggletip label="Why this is read only">{tip(refusalReason('evidence.review'))}</Toggletip>
          </>
        )}
      </div>

      {!raiseOpen && !open && refusal}

      {ordered.length > 0 && (
        <ul className="prose-rows">
          {ordered.map((c) => {
            const label = clarifyLabel(c.status)
            return (
              <li className="prose-row" key={c.id}>
                <span className="prose-mono">{c.questionKey}</span>
                <span className="prose-row-main">
                  <span className="prose-clamp">{c.asked}</span>
                  <span className="prose-sub">
                    {c.askedBy}, {shortDate(c.askedAt)}
                  </span>
                </span>
                {c.roundNo > 1 && <Status tone="neutral">Round {c.roundNo}</Status>}
                <Status tone={label.tone}>{label.text}</Status>
                <button type="button" className="prose-link" onClick={() => setOpenId(c.id)}>
                  {!c.settled && canAsk ? 'Open and decide' : 'Open'}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <Dialog
        shape="drawer"
        kind="record"
        open={open !== null}
        onClose={() => setOpenId(null)}
        title={open ? 'Clarification on ' + open.questionKey : 'Clarification'}
        busy={busy}
        primary={
          open && deciding && open.status === 'ANSWERED'
            ? { label: 'Accept this answer', onClick: () => decide(open, 'accept'), actionId: 'tprm.assessment.a03' }
            : undefined
        }
        more={
          open && deciding ? (
            <>
              <button type="button" disabled={busy} onClick={() => decide(open, 'withdraw')} data-action="tprm.assessment.a05">
                Withdraw the question
              </button>
              {open.status === 'ANSWERED' && (
                <button type="button" disabled={busy} onClick={() => decide(open, 'reject')} data-action="tprm.assessment.a04">
                  Reject it
                </button>
              )}
            </>
          ) : undefined
        }
      >
        {open && (
          <div className="prose-drawer-stack">
            <div className="prose-chips">
              <Status tone={clarifyLabel(open.status).tone}>{clarifyLabel(open.status).text}</Status>
              {open.roundNo > 1 && <Status tone="neutral">Round {open.roundNo}</Status>}
            </div>
            {open.blocking && <p className="prose-sub">{open.blocking}</p>}
            {open.questionPrompt && (
              <div>
                <h3>The question</h3>
                <p className="prose-quote">{open.questionPrompt}</p>
              </div>
            )}
            <div>
              <h3>What we asked</h3>
              <div className="prose-quote">
                <span className="prose-sub">
                  {open.askedBy}, {shortDate(open.askedAt)}
                </span>
                {open.asked}
              </div>
            </div>
            <div>
              <h3>What came back</h3>
              {open.response ? (
                <div className="prose-quote">
                  <span className="prose-sub">
                    {open.respondedBy}, {shortDate(open.respondedAt)}
                  </span>
                  {open.response}
                </div>
              ) : (
                <Status tone="plain">Nothing yet</Status>
              )}
            </div>
            {open.decisionNote && (
              <div>
                <h3>Decision</h3>
                <div className="prose-quote">
                  <span className="prose-sub">
                    {open.decidedBy}, {shortDate(open.decidedAt)}
                  </span>
                  {open.decisionNote}
                </div>
              </div>
            )}

            {refusal}

            {deciding && (
              <div className="field">
                <label htmlFor={'cl-note-' + open.id}>
                  {open.status === 'ANSWERED' ? 'Note on your decision' : 'Why you are withdrawing it'}
                </label>
                <textarea
                  id={'cl-note-' + open.id}
                  rows={3}
                  value={notes[open.id] ?? ''}
                  onChange={(e) => setNotes((n) => ({ ...n, [open.id]: e.target.value }))}
                />
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        shape="modal"
        kind="form"
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        title="Ask about an answer"
        busy={busy}
        primary={{
          label: busy ? 'Sending' : 'Raise this clarification',
          form: 'clarify-ask-form',
          off: (!questionKey || !asked.trim()) && 'Choose the answer and say what you want from them',
          actionId: 'tprm.assessment.a02',
        }}
        more={
          <Toggletip label="What happens next">
            The vendor sees the question against that one answer. The assessment cannot be scored until the round is
            settled.
          </Toggletip>
        }
      >
        <form id="clarify-ask-form" onSubmit={raise} noValidate className="prose-drawer-stack">
          {refusal}
          <div className="field">
            <label htmlFor="cl-key">Which answer</label>
            <Picker
              bare
              id="cl-key"
              label="Which answer"
              value={questionKey}
              options={choices}
              placeholder="Search by key or by the question's words"
              noun="question"
              onChange={setQuestionKey}
            />
          </div>
          <div className="field">
            <label htmlFor="cl-asked">What you want from them</label>
            <textarea id="cl-asked" rows={4} value={asked} onChange={(e) => setAsked(e.target.value)} />
          </div>
        </form>
      </Dialog>
    </section>
  )
}

// ------------------------------------------------------- band override

// The bands a person may set by hand. INSUFFICIENT_EVIDENCE is deliberately
// absent: it is a statement about how much of the questionnaire came back, and
// the API refuses it for the same reason.
const OVERRIDABLE = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

type OverrideDraft = { band: string; reason: string; approvedBy: string; expiresOn: string }

const EMPTY_OVERRIDE: OverrideDraft = { band: '', reason: '', approvedBy: '', expiresOn: '' }

// A standing override, with as much of it as the source we read can say. The
// assessment record carries the band and the reason; the receipt the override
// endpoint returns carries the two people and the expiry as well.
type Standing = {
  band: string
  reason?: string
  by?: string
  approvedBy?: string
  expiresOn?: string
  full: boolean
}

function isoDay(offsetDays: number): string {
  const d = new Date()
  // Midday keeps the date from shifting by one when the browser is west of UTC.
  d.setHours(12, 0, 0, 0)
  d.setDate(d.getDate() + offsetDays)
  return d.toISOString().slice(0, 10)
}

// Distance to the expiry date, and nothing about what happens there. Nothing in
// the estate acts on override_expires_on: no job clears it, no read suppresses
// an override past it. Wording it as "lapsed 4 days ago" beside an override
// that is still being served would report an expiry somebody has to perform as
// one the system already performed.
function expiryText(expiresOn: string | undefined): string {
  const n = daysUntil(expiresOn)
  if (n === null) return ''
  if (n < 0) return Math.abs(n) + (Math.abs(n) === 1 ? ' day past' : ' days past')
  if (n === 0) return 'today'
  return 'in ' + n + (n === 1 ? ' day' : ' days')
}

// The field names match the API's own issue fields, so a check made here and a
// 422 from the server land under the same control with the same wording.
function checkOverride(d: OverrideDraft, computedBand: string, subject: string): Issue[] {
  const out: Issue[] = []
  if (!d.band) {
    out.push({ field: 'band', code: 'REQUIRED', detail: 'Choose the band this vendor should sit in.' })
  } else if (d.band === computedBand) {
    out.push({
      field: 'band',
      code: 'NO_CHANGE',
      detail: 'The model already computed ' + computedBand + '. An override that changes nothing is not a decision.',
    })
  }
  if (!d.reason.trim()) {
    out.push({ field: 'reason', code: 'REQUIRED', detail: 'Say why the computed band is wrong. An audit reads this.' })
  }
  if (!d.approvedBy.trim()) {
    out.push({ field: 'approvedBy', code: 'REQUIRED', detail: 'Name the second person who agreed to it.' })
  } else if (subject !== '' && d.approvedBy.trim() === subject) {
    out.push({
      field: 'approvedBy',
      code: 'SELF_APPROVAL',
      detail: 'You are signed in as ' + subject + '. The approver has to be somebody else.',
    })
  }
  if (!d.expiresOn) {
    out.push({ field: 'expiresOn', code: 'REQUIRED', detail: 'An override without an expiry is never revisited.' })
  } else if (d.expiresOn <= isoDay(0)) {
    out.push({ field: 'expiresOn', code: 'IN_THE_PAST', detail: 'The expiry has to be a date in the future.' })
  }
  return out
}

function receiptFrom(raw: Record<string, unknown>): Standing | null {
  const text = (k: string): string | undefined => (typeof raw[k] === 'string' ? (raw[k] as string) : undefined)
  const band = text('overrideBand')
  if (!band) return null
  return {
    band,
    reason: text('overrideReason'),
    by: text('overrideBy'),
    approvedBy: text('overrideApprovedBy'),
    expiresOn: text('overrideExpiresOn'),
    full: true,
  }
}

function Steps({ steps }: { steps: ScoreStep[] }) {
  return (
    <ol className="prose-steps">
      {steps.map((s, i) => (
        <li key={s.step + i} className={s.gateKey ? 'prose-gate' : undefined}>
          <b>
            {stepLabel(s.step)}
            {s.gateKey && <span className="prose-mono"> {s.gateKey}</span>}
          </b>
          <p>{s.detail}</p>
          {/* A gate that changes a band without naming its instrument is an
              assertion. The citation is what makes it a finding. */}
          {s.citation && <span className="prose-cite">{s.citation}</span>}
        </li>
      ))}
    </ol>
  )
}

// ------------------------------------------------------- the screen

export function AssessmentReview({ assessmentId }: { assessmentId: string }) {
  const [detail, setDetail] = useState<AssessmentDetail | null>(null)
  const [score, setScore] = useState<Score | undefined>(undefined)
  const [error, setError] = useState<unknown>(null)
  const [scoreError, setScoreError] = useState<ApiError | null>(null)
  const [me, setMe] = useState<Me | null>(null)
  const [draft, setDraft] = useState<OverrideDraft>(EMPTY_OVERRIDE)
  const [issues, setIssues] = useState<Listed[]>([])
  const [overrideError, setOverrideError] = useState<ApiError | null>(null)
  const [receipt, setReceipt] = useState<Standing | null>(null)
  const [catalogue, setCatalogue] = useState<RiskTypeCatalogueEntry[] | null>(null)
  // Scoring raises and refreshes findings in the same transaction that writes
  // the score, so the panel below is stale the moment a rescore returns.
  const [findingsVersion, setFindingsVersion] = useState(0)

  // A question key handed from a response row to the clarification form.
  const [clarifyKey, setClarifyKey] = useState('')

  // What the clarifications panel last loaded, and the files the vendor
  // attached, both read by the answers table (release 3.3). The files are
  // null when this person may not read evidence: the table then cannot say a
  // file is missing, and does not.
  // Null until the panel has loaded them, so the tab shows no count rather
  // than a zero it did not read.
  const [clarifications, setClarifications] = useState<Clarification[] | null>(null)
  const [clarificationsFailed, setClarificationsFailed] = useState(false)
  const clarificationsFailedNow = useCallback(() => setClarificationsFailed(true), [])
  const [evidence, setEvidence] = useState<{ questionKey: string; fileName: string }[] | null>(null)
  const [evidenceFailed, setEvidenceFailed] = useState(false)
  // The part of the page on screen, in the address (pushed: Back steps out).
  const [tabParam, setTab] = useHashParam('tab', 'push')
  const [stepsOpen, setStepsOpen] = useState(false)
  const [typeOpen, setTypeOpen] = useState<string | null>(null)
  const [aboutQuestion, setAboutQuestion] = useState<Question | null>(null)
  const [overrideOpen, setOverrideOpen] = useState(false)
  // The Score panel, opened from the header.
  const [scoreOpen, setScoreOpen] = useState(false)
  const scoreSave = useSave(scoreOpen, 'Score')
  const overrideSave = useSave(scoreOpen && overrideOpen, 'Band override')
  const scoring = scoreSave.busy
  const overriding = overrideSave.busy

  // The score carries a type by code, and the name is the API's to give. The
  // catalogue is fetched only once a typed reading is on screen, because the
  // shipped questionnaire types nothing and most assessments never need it.
  const typedCount = score?.riskTypes?.length ?? 0
  useEffect(() => {
    if (typedCount === 0 || catalogue !== null || !can('config.read')) return
    let live = true
    api
      .riskTypes()
      .then((r) => {
        if (live) setCatalogue(r.riskTypes)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [typedCount, catalogue])

  const typeName = (code: string): string =>
    catalogue?.find((t) => t.code === code)?.name ??
    code.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())

  // The override endpoint refuses an approver equal to the calling subject, so
  // the screen reads who it is signed in as and says that before the request
  // rather than after a 409.
  useEffect(() => {
    let live = true
    api
      .me()
      .then((m) => {
        if (live) setMe(m)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    let live = true
    setDetail(null)
    setError(null)
    setDraft(EMPTY_OVERRIDE)
    setIssues([])
    setOverrideError(null)
    setReceipt(null)
    api
      .assessment(assessmentId)
      .then((d) => {
        if (!live) return
        setDetail(d)
        setScore(d.score)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [assessmentId])

  const answers = useMemo(() => {
    const m = new Map<string, AnswerRow>()
    for (const r of detail?.responses ?? []) m.set(r.questionKey, r)
    return m
  }, [detail])

  // The evidence the vendor attached, for the table's Evidence column and the
  // tab's count. AssessmentEvidence reads the same list for its panel.
  useEffect(() => {
    if (!can('evidence.read')) return
    let live = true
    request<{ evidence: { questionKey: string; fileName: string }[] }>(
      '/v1/assessments/' + encodeURIComponent(assessmentId) + '/evidence',
    )
      .then((l) => {
        if (live) setEvidence(l.evidence ?? [])
      })
      .catch(() => {
        // Said in the table and its drawer ("Could not be read"), never
        // shown as "none".
        if (live) setEvidenceFailed(true)
      })
    return () => {
      live = false
    }
  }, [assessmentId])

  // A rescore fires new rules and raises findings: read the assessment again,
  // quietly, so the table's marks and the Findings tab's count follow.
  useEffect(() => {
    if (findingsVersion === 0) return
    let live = true
    api
      .assessment(assessmentId)
      .then((d) => {
        if (live) setDetail(d)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [assessmentId, findingsVersion])

  const rows = useMemo(
    () =>
      detail
        ? reviewRows({
            questions: detail.questions,
            responses: detail.responses,
            lastTime: detail.lastTime,
            questionFlags: detail.questionFlags,
            evidence,
            clarifications: clarifications ?? [],
            deltaSections: detail.assessment.isDelta ? detail.assessment.deltaSectionKeys : null,
            day: shortDate,
          })
        : [],
    [detail, evidence, clarifications],
  )

  // Stable so the child's effect does not re-run on every render of this view,
  // which would clear the select the moment somebody typed into the box below it.
  const clearClarifyKey = useCallback(() => setClarifyKey(''), [])

  function rescore() {
    setScoreError(null)
    scoreSave.send(api.score(assessmentId), {
      done: (fresh) => {
        setScore(fresh)
        setFindingsVersion((n) => n + 1)
        // Scoring writes a new score row and the override columns are not copied
        // on to it, so anything standing has just lapsed. Carrying the receipt
        // over would show an override the API no longer holds.
        setReceipt(null)
      },
      failed: (err) => setScoreError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))),
    })
  }


  function applyOverride(e: FormEvent) {
    e.preventDefault()
    if (!score) return
    const found = checkOverride(draft, score.finalBand, me?.subject ?? '')
    setIssues(found)
    setOverrideError(null)
    if (found.length > 0) return
    overrideSave.send(api.overrideBand(assessmentId, overrideBody(draft)), {
      done: (raw) => {
        setReceipt(receiptFrom(raw))
        setDraft(EMPTY_OVERRIDE)
        setOverrideOpen(false)
      },
      failed: (err) => {
        const failure = err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
        setIssues(fromServer(failure.issues))
        // A refusal that names no field is not a field problem. 403, 404, 409 and
        // anything the network did belong in the box, not under a control.
        setOverrideError(failure.issues.length > 0 ? null : failure)
      },
    })
  }

  // An edit to the override form: what is typed, and the problems that are
  // still problems after it.
  function edit(field: keyof typeof draft, value: string) {
    const next = { ...draft, [field]: value }
    setDraft(next)
    setIssues((list) => afterEdit(list, field, score ? checkOverride(next, score.finalBand, me?.subject ?? '') : []))
  }


  if (error != null) return <ErrorBox error={error} what="This assessment" />
  if (!detail) return <Loading what="the assessment" />

  const a = detail.assessment
  const total = detail.questions.length
  const answered = detail.questions.filter((q) => isAnswered(answers.get(q.questionKey))).length

  const standing: Standing | null =
    receipt ?? (score?.overrideBand ? { band: score.overrideBand, reason: score.overrideReason, full: false } : null)
  const issueFor = (field: string): Issue | undefined => issues.find((i) => i.field === field)
  const closeOverride = () => {
    setDraft(EMPTY_OVERRIDE)
    setIssues([])
    setOverrideError(null)
    setOverrideOpen(false)
  }
  const canOverride = can('risk.approve')
  const canScore = can('assessment.score')
  const canAsk = can('evidence.review')
  const status = statusLabel(a)
  const dueIn = daysUntil(a.dueOn)
  const needing = rows.filter((r) => r.reasons.length > 0).length
  const holding = (clarifications ?? []).filter((c) => !c.settled).length
  // The Evidence part is for people who may read the vendor's files; for
  // anybody else it would open on nothing, so it is not offered.
  const evidenceKnown = can('evidence.read')
  // What the table may say about the files: known, still loading, failed to
  // load, or not this person's to read.
  const evidenceState: EvidenceState = !evidenceKnown ? 'denied' : evidence ? 'known' : evidenceFailed ? 'failed' : 'loading'
  const reviewTabs: PageTab<ReviewTab>[] = [
    { key: 'answers', label: 'Answers', count: total, ...(needing > 0 ? { alert: needing + ' to review' } : {}) },
    ...(evidenceKnown ? [{ key: 'evidence' as const, label: 'Evidence', ...(evidence ? { count: evidence.length } : {}) }] : []),
    {
      key: 'clarifications',
      label: 'Clarifications',
      ...(clarifications ? { count: clarifications.length } : {}),
      ...(clarificationsFailed
        ? { alert: 'Could not be read' }
        : holding > 0
          ? { alert: holding + ' holding the score' }
          : {}),
    },
    { key: 'findings', label: 'Findings', count: detail.findings.length },
    // What the vendor did (release 3.3, item 35): sent, signed in, started,
    // submitted. Last, because it is where a reviewer looks before chasing,
    // not where they work.
    { key: 'activity', label: 'Vendor activity' },
  ]
  const tab: ReviewTab = isReviewTab(tabParam) && (tabParam !== 'evidence' || evidenceKnown) ? tabParam : 'answers'

  const fieldIssue = (field: string, id: string) =>
    issueFor(field) && (
      <p className="field-issue" id={id}>
        <span className="field-issue-code">{issueFor(field)?.code}</span>
        {issueFor(field)?.detail}
      </p>
    )

  return (
    <div className="prose-page">
      <div className="prose-head">
        <div className="prose-head-main">
          <Crumbs
            className="prose-crumb"
            home={{ label: 'Vendors', href: '#/vendors' }}
            trail={[{ label: a.vendorName || 'Vendor', href: '#/vendor/' + encodeURIComponent(a.vendorId) }]}
            current={'Questionnaire ' + a.reference}
          />
          <h1>{a.vendorName || a.reference}</h1>
          <div className="prose-meta">
            <Status tone={status.tone}>{status.text}</Status>
            <span className={dueIn !== null && dueIn < 0 && OUT.has(a.status) ? 'as-due as-late' : 'as-due'}>
              {a.dueOn ? 'Due ' + shortDate(a.dueOn) : 'No due date'}
            </span>
          </div>
        </div>
        {/* The one way into scoring. The panel shows the computed band with
            any override beside it, never over it. */}
        <div className="prose-actions">
          <button type="button" className="primary" onClick={() => setScoreOpen(true)}>
            Score
          </button>
        </div>
      </div>

      <p className="as-progress" aria-label="Where this questionnaire is">
        {progressLine(a, answeredLine(rows), score)}
        {a.isDelta && (
          <>
            <span className="prose-tag">Delta</span>
            <Toggletip hover label="Which sections this delta covers">
              {tip('This delta reissues ' + (a.deltaSectionKeys.join(', ') || 'unnamed sections') + '.')}
            </Toggletip>
          </>
        )}
      </p>

      {/* The page in five parts, one on screen at a time (release 3.3): the
          answers as a table, then what the vendor attached, the questions
          asked about answers, what the score raised and what the vendor did. Every part stays
          mounted, hidden, so each keeps its state and its own loading; what
          is waiting in a hidden part is said on its tab. */}
      <PageTabs label="Assessment" tabs={reviewTabs} value={tab} onChange={(t) => setTab(t === 'answers' ? null : t)}>
        <div hidden={tab !== 'answers'}>
          {total === 0 ? (
            <div className="prose-empty">
              <Status tone="warn">No questions</Status>
              This version has no questions
              <Toggletip label="Why there are no questions">
                The version was published empty, or the assessment points at a version this tenant cannot read.
              </Toggletip>
            </div>
          ) : (
            <>
              {clarificationsFailed && (
                <p className="note warn" role="status">
                  The questions asked about answers could not be read, so a vendor reply waiting for you is not marked here.
                </p>
              )}
              <AnswerTable rows={rows} lastTime={detail.lastTime} canAsk={canAsk} evidence={evidenceState} onAsk={setClarifyKey} />
            </>
          )}
        </div>

        {/* The evidence the vendor attached, and the questions asked about
            answers, stay with the answers. A clarification is a question
            nobody has answered yet, and the unanswered question is what holds
            a score. */}
        <div hidden={tab !== 'evidence'}>
          <AssessmentEvidence assessmentId={a.id} />
        </div>

        <div hidden={tab !== 'clarifications'}>
          <AssessmentClarifications
            assessmentId={a.id}
            questions={detail.questions}
            prefillKey={clarifyKey}
            onPrefillUsed={clearClarifyKey}
            onLoaded={setClarifications}
            onFailed={clarificationsFailedNow}
          />
        </div>

        <div hidden={tab !== 'findings'}>
          <AssessmentFindings assessmentId={a.id} refreshKey={findingsVersion} />
        </div>

        <div hidden={tab !== 'activity'}>
          <AssessmentActivity assessmentId={a.id} />
        </div>
      </PageTabs>

      {/* Scoring, in one panel: the band and why, score now or rescore, the
          engine's own steps, and the override. The steps and the override
          form open inside the panel rather than in a second drawer on top of
          it, so there is one panel to close and one place focus returns from. */}
      <Dialog
        shape="drawer"
        kind="record"
        open={scoreOpen}
        onClose={() => setScoreOpen(false)}
        title={
          <>
            Score
            {score && <BandWithAttestation band={score.finalBand} attestationPct={score.attestationPct} />}
            {standing && (
              <>
                <span className="prose-sub">overridden to</span>
                <BandChip band={standing.band} title="Set by a person, not by the model" />
              </>
            )}
          </>
        }
        busy={scoring || overriding}
        problems={overrideOpen ? issues.length : 0}
        // With the override form open, Cancel puts the form away and leaves
        // the panel open, as its own Cancel did.
        cancel={overrideOpen ? { onClick: closeOverride } : undefined}
        primary={
          !score
            ? {
                label: scoring ? 'Scoring' : 'Score now',
                off: !canScore ? refusalReason('assessment.score') : answered === 0 && 'Nothing has been answered yet',
                onClick: rescore,
                actionId: 'tprm.assessment.a06',
              }
            : overrideOpen
              ? {
                  label: overriding ? 'Applying' : standing ? 'Replace the override' : 'Apply the override',
                  form: 'score-override-form',
                  off: !canOverride && refusalReason('risk.approve'),
                  actionId: 'tprm.assessment.a07',
                }
              : undefined
        }
      >
        <div className="as-score">
          {!score && (
            <div className="prose-drawer-stack">
              <p className="prose-line">
                <Status tone="warn">Not scored</Status>
                No band is claimed yet
              </p>
            </div>
          )}

          {score && (
            <>
              <section className="as-section" aria-labelledby="why-heading">
                <div className="prose-panelhead">
                  <h3 id="why-heading" className="prose-grow">
                    Why this band
                  </h3>
                  {score.computedAt && <span className="prose-mono">{shortDate(score.computedAt)}</span>}
                </div>
                {score.explanation.length === 0 ? (
                  <p className="prose-line">
                    <Status tone="warn">No derivation</Status>
                    Rescore to regenerate it
                    <Toggletip label="Why a derivation matters">
                      The engine recorded no steps for this result, and a band without a derivation is not reviewable.
                    </Toggletip>
                  </p>
                ) : (
                  <BandFactors score={score} />
                )}

                {(score.blockApproval || score.needsException.length > 0) && (
                  <div className="prose-block">
                    {score.blockApproval && (
                      <p className="prose-line">
                        <Status tone="crit">Approval blocked</Status>
                        A rule no approver may sign past
                        <Toggletip hover label="About the blocked approval">
                          The finding it raised is on the page, with the rule's citation. Clear it, or accept it through a
                          risk exception.
                        </Toggletip>
                      </p>
                    )}
                    {score.needsException.length > 0 && (
                      <p className="prose-line">
                        <Status tone="warn">Exception needed</Status>
                        <span className="prose-mono">{score.needsException.join(', ')}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* One reading per risk type, each with the ratio its band was
                    built on. The rollup that turns these into the vendor band is
                    a step in the derivation, not a rule held here. */}
                {(score.riskTypes ?? []).length > 0 && (
                  <div className="prose-block">
                    <h3>By risk type</h3>
                    {(score.riskTypes ?? []).map((t) => (
                      <div className="prose-typerow" key={t.riskType}>
                        <span>{typeName(t.riskType)}</span>
                        <BandChip band={t.finalBand} />
                        <span className="prose-sub">
                          <CoverageCell value={pctValue(t.coveragePct)} />
                          {t.gatesFired.length > 0 && <span className="prose-mono">{t.gatesFired.join(', ')}</span>}
                          {t.explanation.length > 0 && (
                            <button
                              type="button"
                              className="prose-link"
                              aria-expanded={typeOpen === t.riskType}
                              onClick={() => setTypeOpen(typeOpen === t.riskType ? null : t.riskType)}
                            >
                              {typeOpen === t.riskType ? 'Hide why' : 'Why'}
                            </button>
                          )}
                        </span>
                        {typeOpen === t.riskType && (
                          <div className="as-inline-steps">
                            <Steps steps={t.explanation} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="prose-block prose-buttons">
                  <button
                    type="button"
                    onClick={rescore}
                    disabled={scoring || !canScore}
                    title={canScore ? undefined : refusalReason('assessment.score')}
                  >
                    {scoring ? 'Rescoring' : 'Rescore from current answers'}
                  </button>
                  {score.explanation.length > 0 && (
                    <button
                      type="button"
                      className="prose-link"
                      aria-expanded={stepsOpen}
                      onClick={() => setStepsOpen((v) => !v)}
                    >
                      {stepsOpen ? 'Hide how the band was reached' : 'How the band was reached'}
                    </button>
                  )}
                </div>

                {stepsOpen && (
                  <div className="prose-drawer-stack as-inline-steps">
                    <dl className="prose-kv">
                      <dt>Final band</dt>
                      <dd>
                        <BandChip band={score.finalBand} />
                      </dd>
                      <dt>Rules fired</dt>
                      <dd>{score.gatesFired.length > 0 ? <span className="prose-mono">{score.gatesFired.join(', ')}</span> : 'none'}</dd>
                      <dt>Input hash</dt>
                      <dd className="prose-mono">{score.inputHash || 'not recorded'}</dd>
                      {score.computedAt && (
                        <>
                          <dt>Computed</dt>
                          <dd className="prose-mono">{shortDate(score.computedAt)}</dd>
                        </>
                      )}
                      {score.overrideBand && (
                        <>
                          <dt>Overridden to</dt>
                          <dd>
                            {score.overrideBand}
                            {score.overrideReason && <span className="prose-sub"> {score.overrideReason}</span>}
                          </dd>
                        </>
                      )}
                    </dl>
                    <Steps steps={score.explanation} />
                  </div>
                )}
              </section>

              <section className="as-section" aria-labelledby="ovr-heading">
                <div className="prose-panelhead">
                  <h3 id="ovr-heading">Band override</h3>
                  <Toggletip hover label="How an override works">
                    A person overruling the model for a stated period, approved by a second person. Nothing clears it on
                    its expiry date, and a rescore does not carry it forward.
                  </Toggletip>
                  <span className="prose-grow" />
                  {canOverride && !overrideOpen && (
                    <button
                      type="button"
                      onClick={() => {
                        setIssues([])
                        setOverrideError(null)
                        setOverrideOpen(true)
                      }}
                    >
                      {standing ? 'Replace the override' : 'Override the band'}
                    </button>
                  )}
                  {!canOverride && (
                    <>
                      <Status tone="neutral" title={refusalReason('risk.approve')}>
                        View only
                      </Status>
                      <Toggletip label="Why you cannot override">{tip(refusalReason('risk.approve'))}</Toggletip>
                    </>
                  )}
                </div>

                <div className="prose-readings">
                  <div className="prose-reading">
                    <span>What the model computed</span>
                    <BandChip band={score.finalBand} title="Computed from these answers by the bound risk model version" />
                  </div>
                  <div className={standing ? 'prose-reading prose-set' : 'prose-reading'}>
                    <span>What the override says</span>
                    {standing ? (
                      <BandChip band={standing.band} title="Set by a person, not by the model" />
                    ) : (
                      <Status tone="plain">No override</Status>
                    )}
                    {standing && (
                      <dl className="prose-kv">
                        <dt>Reason</dt>
                        <dd>{standing.reason || <span className="muted">not recorded</span>}</dd>
                        {standing.full ? (
                          <>
                            <dt>Applied by</dt>
                            <dd>{standing.by || <span className="muted">not recorded</span>}</dd>
                            <dt>Approved by</dt>
                            <dd>{standing.approvedBy || <span className="muted">not recorded</span>}</dd>
                            <dt>Expires</dt>
                            <dd>
                              {standing.expiresOn ? (
                                <span className="prose-chips">
                                  <span className="prose-mono">{shortDate(standing.expiresOn)}</span>
                                  <Status tone={(daysUntil(standing.expiresOn) ?? 0) < 0 ? 'warn' : 'neutral'}>
                                    {expiryText(standing.expiresOn)}
                                  </Status>
                                </span>
                              ) : (
                                <span className="muted">not recorded</span>
                              )}
                            </dd>
                          </>
                        ) : null}
                      </dl>
                    )}
                  </div>
                </div>

                {/* The endpoint returns a note of its own that says the override
                    lapses on the expiry date. It does not: nothing reads
                    override_expires_on back. Repeating it here would put a promise
                    on the screen the tip above contradicts, so the confirmation
                    says only what was actually written. */}
                {receipt && (
                  <p className="prose-line prose-ok" role="status" style={{ marginTop: 12 }}>
                    <Status tone="ok">Applied</Status>
                    Stored beside the computed band, which is untouched
                  </p>
                )}

                {overrideOpen && (
                  <form id="score-override-form" onSubmit={applyOverride} noValidate className="prose-drawer-stack as-override">
                    {/* Not ErrorBox: this was an action that was attempted and
                        refused, not a panel that failed to load. */}
                    {overrideError && (
                      <div className="error-box" role="alert">
                        <h3>The override was not applied ({overrideError.code})</h3>
                        <p>{overrideError.message}</p>
                        <p className="muted">{overrideError.remedy}</p>
                      </div>
                    )}

                    <div className="field">
                      <label htmlFor="ovr-band">Override the band to</label>
                      <select
                        id="ovr-band"
                        autoFocus
                        value={draft.band}
                        aria-invalid={issueFor('band') ? true : undefined}
                        aria-describedby={issueFor('band') ? 'ovr-band-issue' : undefined}
                        onChange={(e) => edit('band', e.target.value)}
                      >
                        <option value="">Choose a band</option>
                        {OVERRIDABLE.map((b) => (
                          <option key={b} value={b}>
                            {b + (b === score.finalBand ? ' (what the model computed)' : '')}
                          </option>
                        ))}
                      </select>
                      {fieldIssue('band', 'ovr-band-issue')}
                    </div>

                    <div className="field">
                      <label htmlFor="ovr-expires">Expires on</label>
                      <input
                        id="ovr-expires"
                        type="date"
                        value={draft.expiresOn}
                        min={isoDay(1)}
                        aria-invalid={issueFor('expiresOn') ? true : undefined}
                        aria-describedby={'ovr-expires-help' + (issueFor('expiresOn') ? ' ovr-expires-issue' : '')}
                        onChange={(e) => edit('expiresOn', e.target.value)}
                      />
                      <p className="hint" id="ovr-expires-help">
                        A near date in the future{draft.expiresOn && expiryText(draft.expiresOn) ? ', ' + expiryText(draft.expiresOn) : ''}
                      </p>
                      {fieldIssue('expiresOn', 'ovr-expires-issue')}
                    </div>

                    <div className="field">
                      <label htmlFor="ovr-reason">Reason</label>
                      <textarea
                        id="ovr-reason"
                        rows={3}
                        value={draft.reason}
                        aria-invalid={issueFor('reason') ? true : undefined}
                        aria-describedby={issueFor('reason') ? 'ovr-reason-issue' : undefined}
                        onChange={(e) => edit('reason', e.target.value)}
                      />
                      {fieldIssue('reason', 'ovr-reason-issue')}
                    </div>

                    <div className="field">
                      <label htmlFor="ovr-approver">Approved by</label>
                      <input
                        id="ovr-approver"
                        type="text"
                        value={draft.approvedBy}
                        placeholder="The second person, who agreed to this"
                        aria-invalid={issueFor('approvedBy') ? true : undefined}
                        aria-describedby={'ovr-approver-help' + (issueFor('approvedBy') ? ' ovr-approver-issue' : '')}
                        onChange={(e) => edit('approvedBy', e.target.value)}
                      />
                      <p className="hint" id="ovr-approver-help">
                        {me ? 'Somebody other than ' + me.subject : 'Somebody other than you'}
                      </p>
                      {fieldIssue('approvedBy', 'ovr-approver-issue')}
                    </div>

                  </form>
                )}
              </section>
            </>
          )}

          {scoreError && (
            <div className="error-box" role="alert">
              <h3>Scoring failed ({scoreError.code})</h3>
              <p>{scoreError.message}</p>
              <p className="muted">{scoreError.remedy}</p>
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        shape="drawer"
        kind="record"
        open={aboutQuestion !== null}
        onClose={() => setAboutQuestion(null)}
        title={aboutQuestion ? 'Question ' + aboutQuestion.questionKey : 'Question'}
      >
        {aboutQuestion && (
          <div className="prose-drawer-stack">
            <p className="prose-quote">{aboutQuestion.prompt}</p>
            {aboutQuestion.helpText && (
              <div>
                <h3>Help for the vendor</h3>
                <p>{aboutQuestion.helpText}</p>
              </div>
            )}
            {aboutQuestion.citations.length > 0 && (
              <div>
                <h3>Citations</h3>
                <div className="prose-chips">
                  {aboutQuestion.citations.map((c) => (
                    <span className="prose-cite" key={c}>
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  )
}
